"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "@/lib/api-client";
import type { PendingValidationUser, FamilyGroup } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, UserCheck, Users, Users2, Mail } from "lucide-react";
import { isStaffRole, isOwnerAdmin, formatDate  } from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";
import { GroupsPanel } from "@/components/booking/groups-panel";
import { InvitationsPanel } from "@/components/booking/invitations-panel";

export function ModeratorView() {
  const user = useAppStore((s) => s.user);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const ownerAdmin = isOwnerAdmin(user?.role);
  const [tab, setTab] = useState(ownerAdmin ? "validation" : "invitations");

  const { data, isLoading } = useQuery({
    queryKey: ["pending-users"],
    queryFn: () => apiGet<{ users: PendingValidationUser[] }>("/api/admin/validate-users"),
    enabled: !!user && (isStaffRole(user.role)),
    refetchInterval: 20000,
  });

  const users = data?.users ?? [];

  const validate = async (id: string, group: "FAMILY" | "GUEST", familyId?: string) => {
    try {
      await apiPatch(`/api/admin/validate-users/${id}`, {
        userGroup: group,
        familyId: group === "FAMILY" ? (familyId ?? null) : null,
      });
      toast.success(t("toast.userValidated"));
      qc.invalidateQueries({ queryKey: ["pending-users"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["families"] });
    } catch (err: any) {
      toast.error(err.message || "Could not validate");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <ShieldCheck className="h-6 w-6 text-violet-600" /> {t("mod.title")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("mod.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invitations" className="gap-1.5">
            <Mail className="h-4 w-4" />
            {t("mod.tabInvitations")}
          </TabsTrigger>
          {ownerAdmin && (
            <TabsTrigger value="validation" className="gap-1.5">
              <UserCheck className="h-4 w-4" />
              {t("mod.tabValidation")} ({users.length})
            </TabsTrigger>
          )}
          {ownerAdmin && (
            <TabsTrigger value="groups" className="gap-1.5">
              <Users2 className="h-4 w-4" />
              {t("mod.tabGroups")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="invitations" className="mt-4">
          <InvitationsPanel />
        </TabsContent>

        {ownerAdmin && (
          <TabsContent value="validation" className="mt-4">
            <ValidationList
              users={users}
              isLoading={isLoading}
              onValidate={validate}
            />
          </TabsContent>
        )}

        {ownerAdmin && (
          <TabsContent value="groups" className="mt-4">
            <GroupsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function ValidationList({
  users,
  isLoading,
  onValidate,
}: {
  users: PendingValidationUser[];
  isLoading: boolean;
  onValidate: (id: string, group: "FAMILY" | "GUEST", familyId?: string) => void;
}) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [familyPickerFor, setFamilyPickerFor] = useState<PendingValidationUser | null>(null);
  const [chosenFamily, setChosenFamily] = useState("");

  const { data: familiesData } = useQuery({
    queryKey: ["families"],
    queryFn: () => apiGet<{ families: FamilyGroup[] }>("/api/admin/families"),
  });
  const families = familiesData?.families ?? [];

  const validateGuest = (id: string) => {
    onValidate(id, "GUEST");
  };

  const confirmFamily = () => {
    if (!familyPickerFor) return;
    onValidate(familyPickerFor.id, "FAMILY", chosenFamily || undefined);
    setFamilyPickerFor(null);
    setChosenFamily("");
  };

  return (
    <>
      {isLoading ? (
        <div className="grid gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="grid place-items-center rounded-xl border border-dashed py-16 text-center">
          <UserCheck className="mb-3 h-10 w-10 text-emerald-500" />
          <p className="font-medium">{t("mod.noPending")}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {users.map((u) => (
            <Card key={u.id}>
              <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.username}
                    </span>
                    <Badge variant="outline" className="text-muted-foreground">
                      @{u.username}
                    </Badge>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    <span>{u.email}</span>
                    {u.phone && <span>· {u.phone}</span>}
                    <span>· {t("mod.signedUp")} {formatDate(u.createdAt, locale)}</span>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => validateGuest(u.id)}
                    className="gap-1.5"
                  >
                    <UserCheck className="h-4 w-4" />
                    {t("group.GUEST")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setFamilyPickerFor(u)}
                    className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Users className="h-4 w-4" />
                    {t("group.FAMILY")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Family picker: validate a user directly into a named family */}
      <Dialog
        open={!!familyPickerFor}
        onOpenChange={(o) => !o && setFamilyPickerFor(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users2 className="h-5 w-5 text-emerald-600" /> {t("mod.chooseFamily")}
            </DialogTitle>
            <DialogDescription>
              {familyPickerFor
                ? `${familyPickerFor.firstName} ${familyPickerFor.lastName} (@${familyPickerFor.username})`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Select value={chosenFamily} onValueChange={setChosenFamily}>
                <SelectTrigger>
                  <SelectValue placeholder={t("groups.noFamilies")} />
                </SelectTrigger>
                <SelectContent>
                  {families.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {families.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("groups.noFamilies")}</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFamilyPickerFor(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={confirmFamily}
              >
                {t("mod.validateInto", { group: t("group.FAMILY") })}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
